import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { useCommunicationTemplates } from '../../context/CommunicationTemplatesContext';
import { useCommunicationTemplateCategories } from '../../context/CommunicationTemplateCategoryContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { useLoading } from '../../context/LoadingContext';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import EmailTempTagsModal from '../common/EmailTempTagsModal';

// Wrapper component to handle CKEditor errors
const SafeCKEditor = ({ config, data, onChange, onReady, onError }) => {
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return (
      <div className="p-4 border border-red-300 rounded-md bg-red-50">
        <p className="text-red-600">Editor failed to load. Please refresh the page.</p>
      </div>
    );
  }

  return (
    <CKEditor
      editor={ClassicEditor}
      config={config}
      data={data}
      onChange={onChange}
      onReady={onReady}
      onError={(error, { willEditorRestart }) => {
        if (willEditorRestart) {
          console.warn('Editor will restart due to error:', error);
        } else {
          setHasError(true);
        }
        if (onError) {
          onError(error, { willEditorRestart });
        }
      }}
    />
  );
};

// Custom upload adapter
class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader; 
  }

  upload() {
    return this.loader.file
      .then(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function() {
          resolve({ default: reader.result });
        };
        reader.onerror = function() {
          reject('Error reading file');
        };
        reader.readAsDataURL(file);
      }));
  }

  abort() {
    // Abort upload process
  }
}

function MyCustomUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader);
  };
}

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  communication_template_category_id: yup.string().required('Category is required'),
  recipient_id: yup.string().required('Recipient is required'),
  subject: yup.string().required('Subject line is required'),
  body: yup.string()
    .required('Body is required')
    .test('not-empty', 'Body is required', value => {
      if (!value) return false;
      // Remove HTML tags and check if there's actual content
      const strippedContent = value.replace(/<[^>]*>/g, '').trim();
      return strippedContent.length > 0;
    }),
  is_default_template: yup.boolean(),
  is_available_in_worker_app: yup.boolean(),
  include_invoice_pdf: yup.boolean(),
  is_active: yup.boolean(),
});

const CommunicationTemplateForm = () => {
  
  const { id } = useParams();
  const navigate = useNavigate();
  const { addCommunicationTemplate, updateCommunicationTemplate, getCommunicationTemplateById,getCommunicationTemplateAdditionalData } = useCommunicationTemplates();
  const { getCommunicationTemplateCategories } = useCommunicationTemplateCategories();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const getRoutePath = useRoutePath();
  const { showLoading, hideLoading } = useLoading();
  const isEdit = !!id;
  const ranOnceRef = useRef(false);
  const hasFetched = useRef(false);
  const [recipients, setRecipients] = useState([]);
  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
     
    return {
      name: data.name || '',
      communication_template_category_id: data.communication_template_category_id || '',
      recipient_id: data.recipient_id || '',
      subject: data.subject || '',
      body: data.body || '',
      is_default_template: data.is_default_template || false,
      is_available_in_worker_app: data.is_available_in_worker_app || false,
      is_include_invoice_pdf: data.is_include_invoice_pdf || false,
      active: data.active ?? true,
      created_at: data.created_at || new Date().toISOString()
    };
  }, []);

  const [getEditData, setGetEditData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    setError,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });

  useEffect(() => {
    const fetchCommunicationTemplateAdditionalData = async () => {
      const response = await getCommunicationTemplateAdditionalData();
      
      if(response?.recipients){
        setRecipients(response?.recipients || []);
      }
    }
    fetchCommunicationTemplateAdditionalData();
  }, [getCommunicationTemplateAdditionalData]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await getCommunicationTemplateCategories(1, { all: true, active: true });
        if (response?.data) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    }
    if(ranOnceRef.current === false){
      loadData();
      ranOnceRef.current = true;
    }
  }, [getCommunicationTemplateCategories]);

  useEffect(() => {
    if(!id){
      reset(getDefaultValues()); 
      setEditorContent('');
    }
  }, [id, reset, getDefaultValues]);

  const fetchTemplateData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getCommunicationTemplateById(id);
        if (response?.data) {
          setGetEditData(response.data);
          // Reset form with fetched data
          reset(getDefaultValues(response.data));
          // Set editor content
          setEditorContent(response.data.body || '');
        }
      } catch (error) {
        console.error('Error fetching template data:', error);
        toast.error('Error fetching template data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getCommunicationTemplateById, showLoading, hideLoading, reset, getDefaultValues]);

  useEffect(() => {
    fetchTemplateData();
  }, [fetchTemplateData]);

  // Handle ResizeObserver errors globally
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
        // Suppress ResizeObserver errors
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const handleErrorResponse = (response) => {
    setIsLoading(false);
    if (response.errors) {
            
      Object.entries(response.errors).forEach(([field, message]) => {
        // Extract actual message
        let errorMessage = '';

        if (Array.isArray(message)) {
          errorMessage = message[0]; // handle array case
        } else if (typeof message === 'object' && message !== null) {
          errorMessage = Object.values(message)[0]; // take the first value from the object
        } else {
          errorMessage = String(message); // fallback
        }

        setError(field, {
          type: 'manual',
          message: errorMessage,
        });

      
        toast.error(`${field}: ${errorMessage}`);
        
      });
    } else if (response.error?.message) {
      setError('general', {
        type: 'manual',
        message: response.error.message,
      });
    
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    try {
      const formData = {
        ...data,
        body: editorContent,
      };
      
      let response;
      if (id) {
        // Update existing template
        response = await updateCommunicationTemplate(id, formData);
      } else {
        // Create new template
        response = await addCommunicationTemplate(formData);
      }
     
      if (response.status === 200 || response.status === 201) {
        toast.success(id ? 'Template updated successfully!' : 'Template created successfully!');
        navigate(getRoutePath('/communication-templates'));
      } else if (response.status === 422) {
        // Handle validation errors
        handleErrorResponse(response);
      } else {
        toast.error('Error saving template');
      }
    } catch (error) {
      toast.error('Error saving template');
      console.error('Error:', error);
    } finally {
      hideLoading();
      setIsLoading(false);
    }
  };
   
  const handleEditorChange = React.useCallback((event, editor) => {
    try {
      const data = editor.getData();
      setEditorContent(data);
      setValue('body', data, { shouldValidate: true });
    } catch (error) {
      console.warn('CKEditor change handler error:', error);
    }
  }, [setValue]);

  const editorConfiguration = {
    toolbar: {
      items: [
        'heading',
        '|',
        'bold',
        'italic',
        'link',
        'bulletedList',
        'numberedList',
        '|',
        'outdent',
        'indent',
        '|',
        'imageUpload',
        'blockQuote',
        'insertTable',
        'undo',
        'redo'
      ]
    },
    image: {
      toolbar: [
        'imageTextAlternative',
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side'
      ],
      upload: {
        types: ['jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff']
      }
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells'
      ]
    },
    language: 'en',
    extraPlugins: [MyCustomUploadAdapterPlugin]
  };
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <div className="mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEdit ? 'Edit Communication Template' : 'Create New Communication Template'}
        </h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
       <div className="w-full lg:w-8/12 bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Template Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Name *</label>
                  <input
                    type="text"
                    {...register('name')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.name ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter Template Name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Category *</label>
                  <select
                    {...register('communication_template_category_id')}
                    value={watch('communication_template_category_id') || ''}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.communication_template_category_id ? 'border-red-500' : ''
                    }`}
                  >
                    <option value="">Select Category</option>
                    {categories?.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.communication_template_category_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.communication_template_category_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Recipient *</label>
                  <select
                    {...register('recipient_id')}
                    value={watch('recipient_id') || ''}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.recipient_id ? 'border-red-500' : ''
                    }`}
                  >
                    <option value="">Select Recipient</option>
                    {recipients?.map((recipient) => (
                      <option key={recipient.id} value={recipient.id}>
                        {recipient.name}
                      </option>
                    ))}
                  </select>
                  {errors.recipient_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.recipient_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject Line *</label>
                  <input
                    type="text"
                    {...register('subject')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.subject ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter Subject Line"
                  />

                  <em className="text-yellow-600"><strong>Note:</strong> If the subject line is not needed, you can enter <strong> ‘null’ </strong> or any placeholder text.</em>

                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                  )}
                </div>
              </div>

              {/* Template Settings */}
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('is_default_template')}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Use As Default Template</label>
                  {watch('is_default_template') && (
                    <span className="ml-2 px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                      Default Template
                    </span>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('is_available_in_worker_app')}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Make Available In Worker App</label>
                  {watch('is_available_in_worker_app') && (
                    <span className="ml-2 px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                      Available in Worker App
                    </span>
                  )}
                </div>
  
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('is_include_invoice_pdf')}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Include PDF Of Associated Invoice(s)</label>
                  {watch('is_include_invoice_pdf') && (
                    <span className="ml-2 px-2 py-1 text-xs font-medium text-purple-800 bg-purple-100 rounded-full">
                      PDF Included
                    </span>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('active')}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Status</label>
                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                    watch('active') 
                      ? 'text-green-800 bg-green-100' 
                      : 'text-red-800 bg-red-100'
                  }`}>
                    {watch('active') ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Template Body */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Body *</label>
              
                <div className={`appearance-none block w-full border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                  errors.body ? 'border-red-500' : ''
                }`}>
                  <SafeCKEditor
                    config={editorConfiguration}
                    data={editorContent}
                    onChange={handleEditorChange}
                    onReady={(editor) => {
                      // You can store the "editor" and use when it is needed.
                      console.log('Editor is ready to use!', editor);
                    }}
                    onError={(error, { willEditorRestart }) => {
                      // If the editor is restarted, the error element is displayed automatically
                      // with some information about the error that occurred.
                      if (willEditorRestart) {
                        console.warn('Editor will restart due to error:', error);
                      }
                    }}
                  />
                </div>
              
              {errors.body && (
                <p className="mt-1 text-sm text-red-600">{errors.body.message}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
              >
                {isLoading ? 'Saving...' : (isEdit ? 'Update Template' : 'Create Template')}
              </button>
            </div>
          </form>
        </div>
        <div className="w-full lg:w-4/12">
          <EmailTempTagsModal  isModal={false}/>
        </div>
      </div>     
      
    </div>
  );
};

export default CommunicationTemplateForm; 